import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Brain, 
  TrendingUp, 
  DollarSign, 
  Clock, 
  Target, 
  Lightbulb,
  CheckCircle,
  AlertTriangle,
  ArrowRight
} from 'lucide-react';

interface Recommendation {
  id: string;
  type: 'optimization' | 'cost_saving' | 'performance' | 'integration' | 'quality';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  effort: 'high' | 'medium' | 'low';
  estimatedSavings?: string;
  priority: number;
  category: string;
  confidence: number;
}

interface Insight {
  id: string;
  title: string;
  description: string;
  trend: 'up' | 'down' | 'stable';
  value: string;
  change: string;
}

export function IntelligentSupplierRecommendations() {
  const [selectedTab, setSelectedTab] = useState('recommendations');

  const recommendations: Recommendation[] = [
    {
      id: '1',
      type: 'cost_saving',
      title: 'Optimize Energy Corp Integration',
      description: 'Switch to batch processing for Energy Corp to reduce API calls by 60% and save on usage costs.',
      impact: 'high',
      effort: 'medium',
      estimatedSavings: '$2,400/month',
      priority: 95,
      category: 'Cost Optimization',
      confidence: 92
    },
    {
      id: '2',
      type: 'performance',
      title: 'Implement Parallel Processing',
      description: 'Enable parallel processing for multiple suppliers to reduce total processing time by 40%.',
      impact: 'high',
      effort: 'high',
      priority: 88,
      category: 'Performance',
      confidence: 87
    },
    {
      id: '3',
      type: 'quality',
      title: 'Enhance Pattern Recognition',
      description: 'Update detection patterns for Utility Services to improve accuracy from 94% to 97%.',
      impact: 'medium',
      effort: 'low',
      priority: 82,
      category: 'Quality',
      confidence: 89
    },
    {
      id: '4',
      type: 'integration',
      title: 'Add New Supplier API',
      description: 'Integrate with PowerGrid APIs to automate 300+ monthly manual entries.',
      impact: 'medium',
      effort: 'medium',
      estimatedSavings: '$1,200/month',
      priority: 76,
      category: 'Integration',
      confidence: 84
    }
  ];

  const insights: Insight[] = [
    {
      id: '1',
      title: 'Processing Efficiency',
      description: 'Overall processing speed has improved significantly',
      trend: 'up',
      value: '2.3s',
      change: '+15%'
    },
    {
      id: '2',
      title: 'Accuracy Rate',
      description: 'Detection accuracy across all suppliers',
      trend: 'up',
      value: '95.8%',
      change: '+2.1%'
    },
    {
      id: '3',
      title: 'Cost per Transaction',
      description: 'Average cost per processed bill',
      trend: 'down',
      value: '$0.12',
      change: '-8%'
    },
    {
      id: '4',
      title: 'Integration Health',
      description: 'API uptime and reliability score',
      trend: 'stable',
      value: '99.2%',
      change: '0%'
    }
  ];

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getEffortColor = (effort: string) => {
    switch (effort) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'cost_saving': return <DollarSign className="h-4 w-4" />;
      case 'performance': return <TrendingUp className="h-4 w-4" />;
      case 'quality': return <Target className="h-4 w-4" />;
      case 'integration': return <Brain className="h-4 w-4" />;
      default: return <Lightbulb className="h-4 w-4" />;
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'down': return <TrendingUp className="h-4 w-4 text-red-600 rotate-180" />;
      case 'stable': return <ArrowRight className="h-4 w-4 text-gray-600" />;
      default: return <ArrowRight className="h-4 w-4" />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Intelligent Recommendations</h1>
          <p className="text-muted-foreground">AI-powered insights and optimization suggestions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">Refresh Analysis</Button>
          <Button>Generate Report</Button>
        </div>
      </div>

      {/* AI Insights Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {insights.map((insight) => (
          <Card key={insight.id}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{insight.title}</CardTitle>
              {getTrendIcon(insight.trend)}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{insight.value}</div>
              <p className="text-xs text-muted-foreground">
                <span className={insight.trend === 'up' ? 'text-green-600' : insight.trend === 'down' ? 'text-red-600' : 'text-gray-600'}>
                  {insight.change}
                </span> {insight.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
          <TabsTrigger value="analysis">AI Analysis</TabsTrigger>
          <TabsTrigger value="trends">Trend Predictions</TabsTrigger>
        </TabsList>

        <TabsContent value="recommendations" className="space-y-6">
          <div className="grid gap-6">
            {recommendations
              .sort((a, b) => b.priority - a.priority)
              .map((rec) => (
                <Card key={rec.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          {getTypeIcon(rec.type)}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{rec.title}</CardTitle>
                          <p className="text-sm text-muted-foreground">{rec.category}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium">Priority: {rec.priority}/100</div>
                        <Progress value={rec.priority} className="w-16 h-2 mt-1" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">{rec.description}</p>
                    
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Impact:</span>
                        <Badge className={getImpactColor(rec.impact)}>{rec.impact}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Effort:</span>
                        <span className={`text-sm font-medium ${getEffortColor(rec.effort)}`}>
                          {rec.effort}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Confidence:</span>
                        <span className="text-sm font-medium">{rec.confidence}%</span>
                      </div>
                      {rec.estimatedSavings && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Savings:</span>
                          <Badge variant="secondary" className="text-green-600">
                            {rec.estimatedSavings}
                          </Badge>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button size="sm">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Implement
                      </Button>
                      <Button variant="outline" size="sm">
                        Learn More
                      </Button>
                      <Button variant="outline" size="sm">
                        Dismiss
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="analysis" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Performance Analysis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Processing Speed</span>
                    <span className="text-sm font-medium">Excellent</span>
                  </div>
                  <Progress value={85} />
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Accuracy Rate</span>
                    <span className="text-sm font-medium">Very Good</span>
                  </div>
                  <Progress value={78} />
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Cost Efficiency</span>
                    <span className="text-sm font-medium">Good</span>
                  </div>
                  <Progress value={65} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Optimization Opportunities</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <div>
                      <div className="font-medium">Batch Processing</div>
                      <div className="text-sm text-muted-foreground">
                        Could reduce API costs by 40%
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Lightbulb className="h-5 w-5 text-blue-600" />
                    <div>
                      <div className="font-medium">Pattern Updates</div>
                      <div className="text-sm text-muted-foreground">
                        Improve accuracy by 3-5%
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    <div>
                      <div className="font-medium">Parallel Processing</div>
                      <div className="text-sm text-muted-foreground">
                        Reduce processing time by 30%
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="trends" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Future Trend Predictions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium mb-2">Next 30 Days</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <div className="text-sm font-medium">Volume Increase</div>
                      <div className="text-2xl font-bold text-green-600">+12%</div>
                      <div className="text-xs text-muted-foreground">
                        Expected bill processing volume
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <div className="text-sm font-medium">Accuracy Target</div>
                      <div className="text-2xl font-bold text-blue-600">97.5%</div>
                      <div className="text-xs text-muted-foreground">
                        Projected accuracy rate
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <div className="text-sm font-medium">Cost Reduction</div>
                      <div className="text-2xl font-bold text-green-600">-8%</div>
                      <div className="text-xs text-muted-foreground">
                        Expected processing costs
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Strategic Recommendations</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 rounded-lg border">
                      <Brain className="h-5 w-5 text-purple-600" />
                      <div>
                        <div className="font-medium">Invest in ML Model Training</div>
                        <div className="text-sm text-muted-foreground">
                          Improve prediction accuracy for seasonal patterns
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg border">
                      <Target className="h-5 w-5 text-green-600" />
                      <div>
                        <div className="font-medium">Expand Integration Portfolio</div>
                        <div className="text-sm text-muted-foreground">
                          Connect with 3 new high-volume suppliers
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg border">
                      <Clock className="h-5 w-5 text-blue-600" />
                      <div>
                        <div className="font-medium">Implement Real-time Processing</div>
                        <div className="text-sm text-muted-foreground">
                          Reduce processing delays to under 1 second
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}